import { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import classNames from 'classnames';
import {
  get,
} from 'lodash';
import {
  API,
  GlobalActions,
  ToastActions,
  CurrencyActions,
  FlightModel,
  NavigationHelper,
  SidePanelContainer,
  SidePanelHeader,
  SidePanelFooter,
  SidePanelContent,
  VibeButton,
  VibeIcon,
  viClose,
  viKeyboardTab,
  viLock,
  viInfinity,
  color,
  withRouter,
} from 'vibeguide';
import C, { lower } from 'constants';
import DirectContent from './OrderTypes/DirectContent';
import ProgrammaticContent from './OrderTypes/ProgrammaticContent';
import AlwaysOnContent from './OrderTypes/AlwaysOnContent';

function FlightSidebar({
  className = '',
  user,
  orderType,
  history,
  queueToast,
  getCurrencies,
  setPanel,
}) {
  const [flight, setFlight] = useState(new FlightModel());

  /**
   * Get the title of the header based on the selected flight type
   */
  const getFlightHeaderIcon = () => {
    switch (orderType) {
      case 'direct':
        return viKeyboardTab;

      case 'programmatic':
        return viLock;

      case 'always-on-pmp':
        return viInfinity;

      default:
        return viClose;
    }
  };

  /**
   * Get the title of the header based on the selected flight type
   */
  const getFlightHeaderTitle = () => {
    switch (orderType) {
      case 'direct':
        return `New Direct ${C.PROPOSAL}`;

      case 'programmatic':
        return `New Programmatic Guaranteed Deal ${C.PROPOSAL}`;

      case 'always-on-pmp':
        return `New Always On (PMP) ${C.PROPOSAL}`;

      default:
        return `New ${C.PROPOSAL}`;
    }
  };

  /**
   * Get content to show in the sidebar based on the selected flight type
   */
  const getContent = () => {
    switch (orderType) {
      case 'direct':
        return (
          <DirectContent
            flight={flight}
            onChange={onChange}
            onChangeDropdown={onChangeDropdown}
            onChangeAdvertiser={onChangeAdvertiser}
          />
        );

      case 'programmatic':
        return (
          <ProgrammaticContent
            flight={flight}
            onChange={onChange}
            onChangeDropdown={onChangeDropdown}
            onChangeAdvertiser={onChangeAdvertiser}
          />
        );

      case 'always-on-pmp':
        return (
          <AlwaysOnContent
            flight={flight}
            onChange={onChange}
            onChangeDropdown={onChangeDropdown}
            onChangeAdvertiser={onChangeAdvertiser}
          />
        );
      
        default:
          return (
            <div>
              Unknown
            </div>
          );
    }
  };

  /**
   * Close the sidebar
   */
  const onClose = (flightId) => {
    setPanel({
      show: false,
    });

    const url = typeof flightId === 'string'
      // navigate to the flight details page if a flightId is provided (after saving)
      ? `/ad-ops/${lower(C.PROPOSAL)}/${flightId}`
      // navigate to the flights list if no flightId is provided (after cancelling)
      : NavigationHelper.updateParams({
          type: null,
        }, {
          keepPage: true,
        });

    history(url);
  };

  /**
   * When fields change, update the flight model
   */
  const onChange = (e) => {
    const {
      target: {
        name,
        value,
      },
    } = e;

    setFlight({
      ...flight,
      [name]: value,
    });
  }

  const onChangeDropdown = ({
    name,
    value,
    // data,
  }) => {
    setFlight({
      ...flight,
      [name]: value,
    });
  };

  const onChangeAdvertiser = (data) => {
    const {
      id,
      value,
    } = data;

    setFlight({
      ...flight,
      advertiserId: id,
      advertiserName: value,
    });
  };

  const onSave = async () => {
    queueToast({
      type: 'info',
      title: 'Saving...',
      allowClose: true,
    });

    try {
      const data = {
        name: flight.name,
        orderType: flight.orderType,
        dealId: flight.dealId,
        opportunityName: flight.opportunityName,
        opportunityId: flight.opportunityId,
        extReferenceId: flight.extReferenceId,
        deliverySystem: flight.deliverySystem,
        adProvider: flight.adProvider,
        advertiserId: flight.advertiserId,
        category: flight.category,
        agency: flight.agency,
        currencyCode: flight.currencyCode,
        programmaticBuyer: flight.programmaticBuyer,
      }

      console.log('onSave', data);

      const flightResponse = await API.Flight.create(data);
      const success = get(flightResponse, '[0].type') === 'FLIGHT.CREATED';

      if (success) {
        const flightId = get(flightResponse, '[0].documentId');
        // const flight = get(flightResponse, '[0].data');

        // if (flightId && flight) {
        //   const newFlight = {
        //     _id: flightId,
        //     ...flight,
        //   };

        //   console.log('onSave newFlight', newFlight);
        // }

        queueToast({
          type: 'success',
          title: 'Saved!',
          allowClose: true,
        });

        // close the sidebar and navigate to the flight details page
        onClose(flightId);

        // tell listening components the object was saved
        // since we redirect on save, no need to refresh the table view
        // document.dispatchEvent(new Event('onSaveFlight'));
      } else {
        console.error(`Error saving ${lower(C.PROPOSAL)} to API`, flightResponse);

        queueToast({
          type: 'error',
          title: `Error Saving ${C.PROPOSAL}`,
          timeout: 10,
          allowClose: true,
        });
      }
    } catch (err) {
      console.error(`Error saving ${lower(C.PROPOSAL)}`, err);

      queueToast({
        type: 'error',
        title: `Error Saving ${C.PROPOSAL}`,
        timeout: 10,
        allowClose: true,
      });
    }
  };

  /**
   * Check if the save button should be disabled
   */
  const disableSave = () => {
    switch (orderType) {
      case 'direct':
        return !flight.name
          || !flight.deliverySystem
          || !flight.advertiserId
          || !flight.currencyCode;

      case 'programmatic':
      case 'always-on-pmp':
        return !flight.name
          || !flight.dealId
          || !flight.deliverySystem
          || !flight.adProvider
          || !flight.advertiserId
          || !flight.currencyCode;

      default:
        return true;
    }
    // const disableSave = !flight.name
    //   || !flight.advertiserId
    //   || !flight.adProvider
    //   || !flight.deliverySystem
    //   || !flight.startDate
    //   || !flight.endDate
    //   || !flight.currencyCode;
  };

  useEffect(() => {
    // fetch supported currencies
    getCurrencies();

    const data = {};

    if (orderType === 'programmatic') {
      // default ad provider to Koddi for programmatic guaranteed deals
      data.adProvider = 'koddi';
    }

    // set the currency to the user default
    setFlight({
      ...flight,
      ...data,
      orderType,
      currencyCode: user.currencyCode,
    });
  }, []);

  return (
    <SidePanelContainer className={classNames('FlightSidebar', className)}>
      <SidePanelHeader
        style={{
          borderBottom: 'none',
        }}
        icons={(
          <VibeIcon
            className="close"
            icon={viClose}
            color={color.manatee}
            hoverColor={color.obsidian}
            size={24}
            onClick={onClose}
          />
        )}
      >
        <VibeIcon
          icon={getFlightHeaderIcon()}
          color={color.gray900}
          size={16}
        />

        <div className="title wrap">
          {getFlightHeaderTitle()}
        </div>
      </SidePanelHeader>

      <SidePanelContent
        style={{
          borderTop: `1px solid ${color.whiteSmoke}`,
          fontSize: 12,
        }}
      >
        {getContent()}
      </SidePanelContent>

      <SidePanelFooter className="panel-footer">
        <VibeButton
          text={`Save ${C.PROPOSAL}`}
          color="secondary"
          disabled={disableSave()}
          onClick={onSave}
        />
      </SidePanelFooter>
    </SidePanelContainer>
  );
}

function mapStateToProps(state) {
  return {
    user: state.login.user,
    currencies: state.currency.list,
  };
}

const mapDispatchToProps = {
  setPanel: GlobalActions.setPanel,
  queueToast: ToastActions.queueToast,
  getCurrencies: CurrencyActions.getCurrencies,
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(FlightSidebar));
